import React, { useEffect, useState } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import { Alert, Button, Checkbox, FormControl, FormControlLabel, Grid, IconButton, RadioGroup } from "@mui/material";
import { LatexEditor } from "../LatexEditor";
import { Add, Close } from "@mui/icons-material";
import { QuestionTypes } from "../../../static/enums/Questions";

export interface ChoiceTextSingleProps {
	prefix?: string;
}

export default function ChoiceTextSingle({ prefix = "" }: ChoiceTextSingleProps) {
	const { resetField, formState, setValue } = useFormContext();
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		if (formState.dirtyFields[`${prefix}class`]) {
			setValue(`${prefix}correct_answer`, []);
			setValue(`${prefix}answer_config`, []);
			resetField(`${prefix}class`, {
				keepDirty: true,
				defaultValue: QuestionTypes.choice_text_single,
			});
		}
		setLoading(false);
	}, []);

	return loading ? null : <ChoiceTextSingleForm prefix={prefix} />;
}

function ChoiceTextSingleForm({ prefix = "" }: ChoiceTextSingleProps) {
	const { control, getValues, setValue } = useFormContext();
	const {
		fields: fields_config,
		append: append_config,
		remove: remove_config,
	} = useFieldArray({
		control,
		name: `${prefix}answer_config`,
	});

	const onAddField = () => append_config({ label: "" });

	const onRemoveField = (index: number) => {
		const answers = getValues(`${prefix}correct_answer`);
		const newAnswers = answers
			.filter((answer: number) => answer !== index + 1)
			.map((answer: number) => {
				if (answer > index + 1) {
					return answer - 1;
				}
				return answer;
			});
		setValue(`${prefix}correct_answer`, newAnswers);
		remove_config(index);
	};

	return (
		<Grid container direction={"column"} sx={(theme) => ({ padding: theme.spacing(1, 0) })}>
			<Alert severity="info" sx={(theme) => ({ margin: theme.spacing(2, 0) })}>
				Выбор одного текстового/TeX варианта: Отмечайте чекбоксами верные варианты
			</Alert>
			<FormControl>
				<RadioGroup>
					{fields_config.map((field, i) => {
						return (
							<Grid
								container
								key={field.id}
								alignItems={"center"}
								wrap={"nowrap"}
								sx={(theme) => ({ padding: theme.spacing(0, 0, 0, 2), marginBottom: theme.spacing(2) })}
							>
								<CustomCheckbox prefix={prefix} index={i} />
								<LatexEditor controllerName={`${prefix}answer_config.${i}.label`} height={"64px"} />
								<Grid item sx={(theme) => ({ paddingLeft: theme.spacing(2) })}>
									<IconButton disabled={fields_config.length < 2} onClick={() => onRemoveField(i)}>
										<Close />
									</IconButton>
								</Grid>
							</Grid>
						);
					})}
				</RadioGroup>
			</FormControl>
			<Button variant={"text"} size={"small"} endIcon={<Add fontSize={"small"} />} onClick={onAddField}>
				Добавить вариант
			</Button>
		</Grid>
	);
}

interface CustomCheckboxProps {
	index: number;
	prefix?: string;
}

function CustomCheckbox({ index, prefix = "" }: CustomCheckboxProps) {
	const { setValue, getValues } = useFormContext();
	const [checked, setChecked] = useState(getValues(`${prefix}correct_answer`).includes(index + 1));

	const onCorrectAnswerChange = (_: React.ChangeEvent<HTMLInputElement>) => {
		setChecked((prevState: boolean) => {
			setValue(`${prefix}correct_answer.${index}`, !prevState ? index + 1 : null);
			return !prevState;
		});
	};

	return (
		<Grid item>
			<FormControlLabel
				control={<Checkbox onChange={onCorrectAnswerChange} checked={checked} color={"primary"} />}
				label={"Верный"}
			/>
		</Grid>
	);
}
